

import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { createPageUrl } from "@/utils";
import ConnectionStatus from "@/components/ui/ConnectionStatus";
import Footer from "@/components/ui/Footer.jsx";
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger
} from "@/components/ui/sidebar";
import {
  BookOpen,
  LayoutDashboard,
  ShoppingCart,
  Package,
  Users,
  Settings,
  Bell,
  LogOut,
  User as UserIcon,
  Store,
  HelpCircle,
  Mail,
  Key
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext.jsx";
import ChangePasswordModal from "../components/ChangePasswordModal.jsx";
import LanguageSwitcher from "../components/ui/LanguageSwitcher.jsx";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NavItems = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const navigationItems = [
    { title: t('nav.dashboard'), url: "/panel/dashboard", icon: LayoutDashboard },
    { title: t('nav.orders'), url: "/panel/orders", icon: ShoppingCart },
    { title: t('nav.print_runs'), url: "/panel/print-runs", icon: Package },
    { title: t('nav.inventory'), url: "/panel/inventory", icon: Package },
    { title: t('nav.customers'), url: "/panel/customers", icon: Users },
    { title: t('nav.waitlist'), url: "/panel/waitlist", icon: Mail },
    { title: t('nav.settings'), url: "/panel/settings", icon: Settings },
    { title: t('nav.store'), url: "/", icon: Store },
    { title: t('nav.help'), url: "/panel/help", icon: HelpCircle },
  ];

  return (
    <SidebarMenu className="space-y-1">
      {navigationItems.map((item) => (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton
            asChild
            className={`hover:bg-amber-50 hover:text-amber-800 transition-all duration-200 rounded-xl mx-1 font-medium ${
              location.pathname === item.url
                ? 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-800 border border-amber-200'
                : 'text-slate-600'
            }`}
          >
            <Link to={item.url} className="flex items-center gap-3 px-4 py-3">
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.title}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );
};

const UserMenu = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-10 w-full justify-start gap-3 px-3" data-testid="user-menu">
          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
            <UserIcon className="w-4 h-4 text-slate-500"/>
          </div>
          <div className="flex-1 text-left">
            <p className="font-semibold text-sm text-slate-800 truncate">
              {user?.full_name || t('user.default_name')}
            </p>
            <p className="text-xs text-slate-500 truncate">
              {user?.email || 'user@example.com'}
            </p>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {user?.full_name || t('user.default_name')}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email || 'user@example.com'}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ChangePasswordModal>
          <DropdownMenuItem 
            className="cursor-pointer"
            onSelect={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Key className="mr-2 h-4 w-4" />
            <span>{t('actions.change_password')}</span>
          </DropdownMenuItem>
        </ChangePasswordModal>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          className="cursor-pointer text-red-600 focus:text-red-600"
          onClick={handleLogout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t('actions.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function Layout({ children, currentPageName }) {
  const { t } = useTranslation();

  return (
    <SidebarProvider>
      <style>
        {`
          :root {
            --primary: #1e293b;
            --primary-light: #334155;
            --accent: #f59e0b;
            --accent-light: #fbbf24;
            --success: #10b981;
            --background: #fefefe;
            --muted: #f8fafc;
            --border: #e2e8f0;
            --text: #0f172a;
            --text-muted: #64748b;
          }

          .gradient-bg {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          }

          .book-shadow {
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }

          .elegant-card {
            background: white;
            border: 1px solid var(--border);
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05);
          }
        `}
      </style>
      <div className="min-h-screen flex w-full gradient-bg">
        <Sidebar className="border-r border-slate-200 bg-white">
          <SidebarHeader className="border-b border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-800 to-slate-600 rounded-xl flex items-center justify-center shadow-lg">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-slate-900 text-lg">BookForge</h2>
                <p className="text-xs text-slate-500 font-medium">{t('app.subtitle')}</p>
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent className="p-3">
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-3">
                {t('app.management')}
              </SidebarGroupLabel>
              <NavItems />
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-slate-200 p-4">
            <div className="space-y-3">
              <LanguageSwitcher />
              <UserMenu />
            </div>
            <p className="text-xs text-center text-slate-400 mt-2">v1.1</p>
          </SidebarFooter>
        </Sidebar>

        <main className="flex-1 flex flex-col">
          <header className="bg-white border-b border-slate-200 px-6 py-4 md:hidden">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <SidebarTrigger className="hover:bg-slate-100 p-2 rounded-xl transition-colors duration-200" />
                <h1 className="text-xl font-bold text-slate-900">BookForge</h1>
              </div>
              <div className="flex items-center gap-2">
                <LanguageSwitcher />
              </div>
            </div>
          </header>

          <div className="flex-1 overflow-auto">
            <ConnectionStatus />
            {children}
          </div>
          <Footer showText={false} />
        </main>
      </div>
    </SidebarProvider>
  );
}

